/**
 * Cmd/Ctrl+K command palette. Searches across nav items and (when the
 * user has companies loaded) the company picker. Keyboard-driven:
 * ArrowUp / ArrowDown to navigate, Enter to run, Esc to close.
 *
 * The trigger button is meant to live at the top of the sidebar; it
 * shows the keyboard hint. The modal opens on click, on Cmd/Ctrl+K
 * (via the global QUICK_SEARCH_TOGGLE_EVENT dispatched from
 * ShortcutsRuntime), or programmatically via `externalOpen`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import BusinessIcon from '@mui/icons-material/Business';
import { SECTIONS, FOOTER_ITEMS, type NavItem } from './navCatalog';
import { useAuthStore } from '../../stores/authStore';
import { useCompanyStore } from '../../stores/companyStore';
import { formatCombo, buildActionToCombo } from './shortcutCatalog';
import { QUICK_SEARCH_TOGGLE_EVENT } from './ShortcutsRuntime';

type ResultKind = 'page' | 'company';

interface ResultItem {
  id: string;
  kind: ResultKind;
  label: string;
  subtitle?: string;
  icon?: React.ReactNode;
  combo?: string;
  run: () => void;
}

const CATEGORY_LABEL: Record<ResultKind, string> = {
  page:    'דפים',
  company: 'חברות',
};
const CATEGORY_ORDER: ResultKind[] = ['page', 'company'];

function matchScore(label: string, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const l = label.toLowerCase();
  if (l.startsWith(q)) return 100;
  if (l.includes(q)) return 60;
  // Loose: every char appears in order
  let pos = 0;
  for (const ch of q) {
    pos = l.indexOf(ch, pos);
    if (pos < 0) return 0;
    pos++;
  }
  return 20;
}

export default function QuickSearch() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = useAuthStore((s) => s.user?.is_admin ?? false);
  const companies = useCompanyStore((s) => s.companies);
  const selectCompany = useCompanyStore((s) => s.selectCompany);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIdx(0);
  }, []);

  useEffect(() => {
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener(QUICK_SEARCH_TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(QUICK_SEARCH_TOGGLE_EVENT, onToggle);
  }, []);

  const actionToCombo = useMemo(buildActionToCombo, []);

  /** All nav items resolved through i18n with their action keys. */
  const allNavItems = useMemo(() => {
    const items: Array<{ item: NavItem; sectionTitle: string }> = [];
    for (const s of SECTIONS) for (const it of s.items) items.push({ item: it, sectionTitle: s.title });
    for (const it of FOOTER_ITEMS) {
      if (it.adminOnly && !isAdmin) continue;
      items.push({ item: it, sectionTitle: 'nav.sections.admin' });
    }
    return items;
  }, [isAdmin]);

  const results: ResultItem[] = useMemo(() => {
    const q = query.trim();
    const out: ResultItem[] = [];

    // Nav items
    for (const { item, sectionTitle } of allNavItems) {
      const label = t(item.label, { defaultValue: item.label });
      const sub = t(sectionTitle, { defaultValue: sectionTitle });
      const score = q ? matchScore(label, q) : 0;
      if (q && score <= 0) continue;
      out.push({
        id: `page:${item.to}`,
        kind: 'page',
        label,
        subtitle: sub,
        icon: item.icon,
        combo: actionToCombo[`nav:${item.to}`],
        run: () => { navigate(item.to); close(); },
      });
    }

    // Companies (top 50 matches)
    const filteredCompanies = q
      ? companies
          .map((c) => ({ c, s: matchScore(c.name || '', q) }))
          .filter((r) => r.s > 0)
          .sort((a, b) => b.s - a.s)
      : companies.slice(0, 20).map((c) => ({ c, s: 0 }));
    for (const { c } of filteredCompanies.slice(0, 50)) {
      out.push({
        id: `company:${c.id}`,
        kind: 'company',
        label: c.name,
        subtitle: c.last_journal_date
          ? `ניתוח אחרון: ${c.last_journal_date}`
          : 'אין נתונים מקומיים',
        icon: <BusinessIcon />,
        run: () => { selectCompany(c.id); close(); },
      });
    }

    // Stable sort by score then preserve input order via insertion
    if (q) {
      out.sort((a, b) => {
        const sa = matchScore(a.label, q);
        const sb = matchScore(b.label, q);
        return sb - sa;
      });
    }
    return out;
  }, [query, allNavItems, companies, t, actionToCombo, navigate, selectCompany, close]);

  // Reset active index when query changes
  useEffect(() => { setActiveIdx(0); }, [query]);
  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // Keep active row in view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      results[activeIdx]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  // Render groups in the canonical order
  const grouped = useMemo(() => {
    const map = new Map<ResultKind, ResultItem[]>();
    for (const r of results) {
      if (!map.has(r.kind)) map.set(r.kind, []);
      map.get(r.kind)!.push(r);
    }
    const ordered: { kind: ResultKind; items: ResultItem[] }[] = [];
    for (const k of CATEGORY_ORDER) {
      const xs = map.get(k);
      if (xs && xs.length) ordered.push({ kind: k, items: xs });
    }
    return ordered;
  }, [results]);

  // Flat index for keyboard navigation that matches the visual order
  const flatVisualOrder = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          mx: 1.5, my: 1,
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.25, py: 0.8,
          borderRadius: 2,
          backgroundColor: '#F1F5F9',
          border: '1px solid #E2E8F0',
          color: '#64748B',
          cursor: 'pointer',
          transition: 'background-color .15s ease, border-color .15s ease',
          '&:hover': { backgroundColor: '#E2E8F0', borderColor: '#CBD5E1' },
        }}
      >
        <SearchIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ flex: 1, fontSize: 13 }}>חיפוש מהיר</Typography>
        <Box sx={{
          fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
          px: 0.65, py: 0.1, borderRadius: 0.75,
          backgroundColor: 'rgba(15,23,42,0.08)',
        }}>{formatCombo('mod+k')}</Box>
      </Box>

      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }} onKeyDown={handleKey}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1.25,
            borderBottom: '1px solid #E2E8F0',
          }}>
            <SearchIcon sx={{ color: '#64748B' }} />
            <InputBase
              inputRef={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="חיפוש דף או חברה…"
              fullWidth
              sx={{ fontSize: 15 }}
            />
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Esc</Typography>
          </Box>

          <Box ref={listRef} sx={{ maxHeight: 460, overflowY: 'auto', py: 1 }}>
            {flatVisualOrder.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                אין תוצאות
              </Typography>
            ) : (
              grouped.map((g) => (
                <Box key={g.kind} sx={{ mb: 1 }}>
                  <Typography sx={{
                    px: 2, py: 0.5,
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em',
                    color: '#94A3B8', textTransform: 'uppercase',
                  }}>
                    {CATEGORY_LABEL[g.kind]}
                  </Typography>
                  {g.items.map((r) => {
                    const idx = flatVisualOrder.indexOf(r);
                    const active = idx === activeIdx;
                    return (
                      <Box
                        key={r.id}
                        data-idx={idx}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={r.run}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.25,
                          px: 2, py: 1,
                          cursor: 'pointer',
                          backgroundColor: active ? '#DBEAFE' : 'transparent',
                          color: active ? '#1E40AF' : '#0F172A',
                        }}
                      >
                        <Box sx={{
                          width: 22, height: 22,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: active ? '#2563EB' : '#64748B',
                          '& svg': { fontSize: 20 },
                        }}>{r.icon}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontSize: 14, fontWeight: active ? 700 : 500,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{r.label}</Typography>
                          {r.subtitle && (
                            <Typography sx={{
                              fontSize: 11, color: '#64748B',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{r.subtitle}</Typography>
                          )}
                        </Box>
                        {r.combo && !active && (
                          <Box sx={{
                            fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
                            px: 0.65, py: 0.1, borderRadius: 0.75,
                            backgroundColor: 'rgba(15,23,42,0.06)',
                            color: '#64748B',
                          }}>{formatCombo(r.combo)}</Box>
                        )}
                        {active && (
                          <KeyboardReturnIcon sx={{ fontSize: 16, color: '#2563EB' }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </Box>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 2, py: 1,
            borderTop: '1px solid #E2E8F0',
            color: '#94A3B8',
            fontSize: 11,
          }}>
            <Box>↑↓ ניווט</Box>
            <Box>↵ בחר</Box>
            <Box sx={{ flex: 1 }} />
            <Box>{formatCombo('mod+k')} לפתיחה</Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
